'use client'

import { PopperContentProps } from '@radix-ui/react-popper'
import { CheckIcon, ChevronDownIcon, SearchIcon } from 'lucide-react'
import * as React from 'react'

import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'

import { Label } from './ui/label'

export interface SelectOption {
  label: string
  value: string
}
export interface AppSelectProps {
  label?: string
  placeholder?: string
  options: SelectOption[]
  value: string
  onChange(value: string): void
  className?: string
  disabled?: boolean
  triggerClassName?: string
  selectValueComponent?: React.ReactNode
  menuPlacement?: PopperContentProps['side']
  avoidCollisions?: boolean
  selectContentClassName?: string
  required?: boolean
  defaultValue?: string
  searchable?: boolean
  searchPlaceholder?: string
  emptyMessage?: string
}

const SearchableSelect = React.forwardRef<HTMLButtonElement, AppSelectProps>(
  (
    {
      label,
      placeholder,
      options,
      value,
      onChange,
      triggerClassName,
      className,
      disabled,
      selectValueComponent,
      menuPlacement,
      avoidCollisions,
      selectContentClassName,
      required,
      defaultValue,
      searchPlaceholder,
      emptyMessage,
    },
    ref
  ) => {
    const [open, setOpen] = React.useState(false)
    const [query, setQuery] = React.useState('')
    const [highlightIndex, setHighlightIndex] = React.useState(0)
    const searchRef = React.useRef<HTMLInputElement>(null)
    const highlightedRef = React.useRef<HTMLButtonElement>(null)
    const safeValue = value || defaultValue || ''
    const selected = options.find(option => option.value === safeValue)

    const filteredOptions = React.useMemo(() => {
      const normalized = query.trim().toLowerCase()
      if (!normalized) return options
      return options.filter(option => option.label.toLowerCase().includes(normalized) || option.value.toLowerCase().includes(normalized))
    }, [options, query])

    React.useEffect(() => {
      setHighlightIndex(0)
    }, [query, open])

    React.useEffect(() => {
      highlightedRef.current?.scrollIntoView({ block: 'nearest' })
    }, [highlightIndex])

    const selectOption = (nextValue: string) => {
      onChange(nextValue)
      setOpen(false)
      setQuery('')
    }

    const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlightIndex(current => Math.min(current + 1, Math.max(filteredOptions.length - 1, 0)))
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlightIndex(current => Math.max(current - 1, 0))
        return
      }
      if (event.key === 'Enter') {
        event.preventDefault()
        const match = filteredOptions[highlightIndex]
        if (match) selectOption(match.value)
      }
    }

    return (
      <div className={cn('relative', className)}>
        {label && (
          <Label className='text-text-primary text-sm'>
            {label}
            {required && <span className='text-gray-400'>*</span>}
          </Label>
        )}
        <Popover
          open={open}
          onOpenChange={nextOpen => {
            setOpen(nextOpen)
            if (!nextOpen) setQuery('')
          }}
        >
          <PopoverTrigger asChild>
            <button
              ref={ref}
              type='button'
              role='combobox'
              aria-expanded={open}
              aria-haspopup='listbox'
              disabled={disabled}
              className={cn(
                'text-text-secondary flex h-full w-full cursor-pointer items-center justify-between gap-2 rounded-full border bg-white !px-3 py-2 text-sm shadow-none',
                !selected && 'text-muted-foreground',
                disabled && 'cursor-not-allowed opacity-50',
                triggerClassName
              )}
            >
              {selectValueComponent || <span className='min-w-0 truncate text-start'>{selected?.label || placeholder || ''}</span>}
              <ChevronDownIcon className='size-4 shrink-0 opacity-50' />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align='start'
            side={menuPlacement}
            avoidCollisions={avoidCollisions}
            className={cn(
              'w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-lg border border-gray-200 bg-white p-0 shadow-md',
              selectContentClassName
            )}
            onOpenAutoFocus={event => {
              event.preventDefault()
              searchRef.current?.focus()
            }}
          >
            <div className='relative border-b p-2'>
              <SearchIcon className='text-muted-foreground pointer-events-none absolute start-4 top-1/2 size-4 -translate-y-1/2' />
              <Input
                ref={searchRef}
                value={query}
                onChange={event => setQuery(event.target.value)}
                onKeyDown={onSearchKeyDown}
                placeholder={searchPlaceholder}
                className='ps-8'
                aria-label={searchPlaceholder}
              />
            </div>
            <div role='listbox' className='max-h-60 overflow-y-auto p-1'>
              {filteredOptions.length === 0 ? (
                <p className='text-muted-foreground px-2 py-1.5 text-sm'>{emptyMessage}</p>
              ) : (
                filteredOptions.map((option, index) => {
                  const isSelected = option.value === safeValue
                  const isHighlighted = index === highlightIndex
                  return (
                    <button
                      key={option.value}
                      ref={isHighlighted ? highlightedRef : undefined}
                      type='button'
                      role='option'
                      aria-selected={isSelected}
                      className={cn(
                        'relative flex w-full cursor-pointer items-center rounded-sm py-1.5 ps-2 pe-8 text-start text-sm break-words whitespace-normal',
                        isHighlighted && 'bg-accent',
                        isSelected && 'font-medium'
                      )}
                      onMouseEnter={() => setHighlightIndex(index)}
                      onClick={() => selectOption(option.value)}
                    >
                      {option.label}
                      {isSelected && (
                        <span className='absolute end-2 flex size-3.5 items-center justify-center'>
                          <CheckIcon className='size-4' />
                        </span>
                      )}
                    </button>
                  )
                })
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    )
  }
)
SearchableSelect.displayName = 'SearchableSelect'

export const AppSelect = React.forwardRef<HTMLButtonElement, AppSelectProps>((props, ref) => {
  if (props.searchable) {
    return <SearchableSelect {...props} ref={ref} />
  }

  const {
    label,
    placeholder,
    options,
    value,
    onChange,
    triggerClassName,
    className,
    disabled,
    selectValueComponent,
    menuPlacement,
    avoidCollisions,
    selectContentClassName,
    required,
    defaultValue,
  } = props
  const safeValue = value || ''

  return (
    <div className={cn('relative', className)}>
      {label && (
        <Label className='text-text-primary text-sm'>
          {label}
          {required && <span className='text-gray-400'>*</span>}
        </Label>
      )}
      <Select defaultValue={defaultValue} value={safeValue} onValueChange={onChange} disabled={disabled}>
        <SelectTrigger
          ref={ref}
          className={cn(
            'text-text-secondary h-full w-full cursor-pointer rounded-full border bg-white !px-3 py-2 shadow-none',
            triggerClassName
          )}
        >
          {selectValueComponent || <SelectValue placeholder={label ? '' : placeholder || ''} />}
        </SelectTrigger>
        <SelectContent
          side={menuPlacement}
          className={cn('overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md', selectContentClassName)}
          avoidCollisions={avoidCollisions}
        >
          <SelectGroup>
            {options.map(option => (
              <SelectItem key={option.value} value={option.value} className='break-words whitespace-normal'>
                {option.label}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
    </div>
  )
})

AppSelect.displayName = 'AppSelect'
